import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Space, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getWorkflowDefinition,
  createWorkflowInstance,
  type WorkflowDefinition,
} from '@/api/workflowApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

export default function WorkflowInstanceForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const definitionId = searchParams.get('definition_id') ?? '';

  const [definition, setDefinition] = useState<WorkflowDefinition | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('workflows', 'write');

  useEffect(() => {
    if (!definitionId) {
      setError(t('workflows.validation.definition_id_required'));
      setLoading(false);
      return;
    }
    setLoading(true);
    getWorkflowDefinition(definitionId)
      .then((def) => setDefinition(def))
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('app.not_found'))
      )
      .finally(() => setLoading(false));
  }, [definitionId, t]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const instance = await createWorkflowInstance({
        definition_id: definitionId,
      });
      message.success(t('workflows.instance_create_success'));
      navigate(`/workflows/instances/${instance.id}`);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('app.error');
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canWrite)
    return <Alert type="warning" message={t('app.forbidden')} />;

  if (loading)
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );

  if (error && !definition) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/workflows')}
        >
          {t('app.back')}
        </Button>
        <Alert type="error" message={error} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div>
      <h2>{t('workflows.action.run')}: {definition?.name}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <div style={{ maxWidth: 600, marginBottom: 24 }}>
        <p>
          {t('workflows.field.trigger_type')}: {definition?.trigger_type ? t(`workflows.trigger_type_options.${definition.trigger_type}`) : '-'}
        </p>
        <p>
          {t('workflows.field.steps')}: {definition?.steps?.length ?? 0}
        </p>
        <p>
          {t('workflows.field.version')}: v{definition?.version}
        </p>
      </div>

      <Space>
        <Button
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          {t('workflows.action.run')}
        </Button>
        <Button
          onClick={() =>
            navigate(`/workflows/definitions/${definitionId}`)
          }
        >
          {t('app.cancel')}
        </Button>
      </Space>
    </div>
  );
}
